import React, { useState } from 'react';
import { ArrowRight, Lock, Fingerprint, Activity } from 'lucide-react';
import { motion } from 'motion/react';

export function ExtractionFunnel() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ name: '', email: '', enterprise: '', intention: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setStep(3);
    }, 2000);
  };

  return (
    <div className="relative bg-gradient-to-b from-indigo-900/20 to-transparent p-5 rounded-xl border border-indigo-500/20 group">
      {/* Animated Border Gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/20 to-indigo-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000 -z-10 blur-xl"></div>
      
      <div className="relative z-0">
        
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center justify-center p-2 bg-cyan-900/20 rounded-full mb-1 glow-border-cyan">
                <Fingerprint className="text-cyan-400" size={16} />
              </div>
              <h2 className="text-lg font-sans font-bold tracking-tight text-white glow-text-cyan uppercase">
                Initiate Protocol
              </h2>
              <p className="text-[10px] font-mono text-slate-400 max-w-md mx-auto leading-tight">
                The £5,000 Extraction Funnel requires clearance for enterprise restructuring.
              </p>
            </div>
            
            <div className="flex justify-center pt-2">
              <button
                onClick={() => setStep(2)}
                className="w-full bg-cyan-600 hover:bg-cyan-500 transition-colors text-white text-[10px] font-bold uppercase tracking-[0.2em] py-2.5 rounded shadow-[0_0_15px_rgba(8,145,178,0.4)] flex justify-center items-center gap-2"
              >
                COMMENCE AUDIT <ArrowRight size={12} />
              </button>
            </div>
            
            <div className="flex items-center justify-center gap-4 mt-4 opacity-50 text-[9px] font-mono tracking-widest text-cyan-200">
              <span className="flex items-center gap-1"><Lock size={8} /> ZERO-DAY</span>
              <span className="flex items-center gap-1"><Activity size={8} /> Q-API</span>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.form 
            initial={{ opacity: 0, x: 10 }} 
            animate={{ opacity: 1, x: 0 }} 
            onSubmit={handleSubmit}
            className="space-y-4"
          >
             <div className="mb-2">
              <h3 className="text-sm font-sans font-bold text-white mb-1 uppercase">Identification Matrix</h3>
              <p className="text-[9px] font-mono text-slate-500">Provide verified telemetry.</p>
             </div>

             <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-[9px] uppercase font-mono text-indigo-400">Authority</label>
                <input 
                  required
                  type="text" 
                  placeholder="AUTHORITY DESIGNATION"
                  className="w-full bg-black/40 border border-white/10 rounded h-8 px-2 text-xs font-mono focus:border-cyan-500/50 outline-none text-white transition-colors"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] uppercase font-mono text-indigo-400">Encrypted Comms</label>
                <input 
                  required
                  type="email" 
                  placeholder="TARGET (EMAIL)"
                  className="w-full bg-black/40 border border-white/10 rounded h-8 px-2 text-xs font-mono focus:border-cyan-500/50 outline-none text-white transition-colors"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] uppercase font-mono text-indigo-400">Enterprise Class</label>
                <select 
                  className="w-full bg-black/40 border border-white/10 rounded h-8 px-2 text-[10px] font-mono focus:border-cyan-500/50 outline-none text-white transition-colors"
                  value={formData.enterprise}
                  onChange={e => setFormData({...formData, enterprise: e.target.value})}
                >
                  <option value="" disabled>Select Classification</option>
                  <option value="Initial Capture">£5,000 Initial Capture</option>
                  <option value="Equity Lock">£50,000 Equity Lock</option>
                </select>
              </div>
             </div>

             <div className="pt-2 flex flex-col gap-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-cyan-600 hover:bg-cyan-500 transition-colors text-white text-[10px] font-bold uppercase tracking-[0.2em] py-2.5 rounded shadow-[0_0_15px_rgba(8,145,178,0.4)] disabled:opacity-50"
                >
                  {isSubmitting ? 'PROCESSING...' : 'CONFIRM EXTRACTION'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setStep(1)}
                  className="text-[9px] font-mono text-slate-500 hover:text-white transition-colors w-full text-center"
                >
                  [ ABORT ]
                </button>
             </div>
          </motion.form>
        )}

        {step === 3 && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-4 py-4">
            <div className="w-10 h-10 rounded-full border-2 border-cyan-500 text-cyan-400 flex items-center justify-center mx-auto shadow-[0_0_20px_rgba(0,240,255,0.4)]">
              <Lock size={18} />
            </div>
            <div>
              <h3 className="text-sm font-sans font-bold text-white glow-text-cyan mb-1 uppercase">EXTRACTION SECURED</h3>
              <p className="text-[10px] font-mono text-slate-400 max-w-sm mx-auto leading-tight">
                Enterprise matrix logged. The Oracle Engine is computing. Stand by.
              </p>
            </div>
            <div className="text-[9px] font-mono text-indigo-400 uppercase tracking-[0.2em] pt-3 border-t border-white/5">
              Secure Payment Provider Vault Validated
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
